/**
 * #39 · Lotes de producción, con retiro del mercado.
 *
 * La columna que importa es "unidades activadas": es cuánto producto de ese
 * lote está realmente en la calle, y es el número que decide el alcance de un
 * retiro. Se muestra tal cual lo devuelve la API (`batch.activated_count`).
 *
 * Lo que NO se muestra, y por qué: **unidades escaneadas por lote**. El issue
 * lo pide, pero hoy ningún endpoint lo devuelve — `BatchOut` no lo trae y
 * `/analytics/overview` agrega por tenant, no por lote. Inventarlo del lado
 * del cliente (sumar escaneos de una lista paginada) daría un número que
 * cambia con el scroll. Un número inventado en una pantalla desde la que se
 * decide un recall es peor que una columna que falta; queda anotado en el
 * reporte del issue como hueco de la API. El `risk_score` del lote, que sí
 * viene y sí lo calcula el motor de anomalías, ocupa esa columna mientras
 * tanto.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { batchKeys, productKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { riskTone } from '../products/catalog';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { Dialog } from '../products/ui/Dialog';
import { SelectField } from '../products/ui/fields';
import { BatchForm, type BatchDraft } from './BatchForm';
import { RecallDialog } from './RecallDialog';
import { BATCH_STATUSES, BATCH_STATUS_LABEL_KEYS, BATCH_STATUS_TONES, isBatchStatus } from './status';

type Batch = Schemas['BatchOut'];
type Product = Schemas['ProductOut'];

const PAGE_SIZE = 50;
/** Tope del desplegable de productos. Un catálogo real son decenas de SKU. */
const PRODUCT_OPTIONS = 200;

export function BatchesPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();

  // Los filtros viven en la URL: un lote sospechoso se comparte por chat con
  // el link, y el otro tiene que ver la misma lista.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawProduct = searchParams.get('product');
  const rawStatus = searchParams.get('status');
  const productId = rawProduct !== null && /^\d+$/.test(rawProduct) ? Number(rawProduct) : null;
  const status = rawStatus !== null && isBatchStatus(rawStatus) ? rawStatus : null;

  const [creating, setCreating] = useState(false);
  const [recalling, setRecalling] = useState<Batch | null>(null);

  const products = useKeysetQuery<Product>(
    productKeys.list({ limit: PRODUCT_OPTIONS }),
    '/api/v1/products',
    undefined,
    { limit: PRODUCT_OPTIONS },
  );

  const batches = useKeysetQuery<Batch>(
    batchKeys.list({ limit: PAGE_SIZE, productId, status }),
    '/api/v1/batches',
    { product_id: productId, status },
    { limit: PAGE_SIZE },
  );
  const { query } = batches;

  const create = useMutation<Batch, unknown, BatchDraft>({
    mutationFn: (draft) => api.post<Batch>('/api/v1/batches', draft),
    onSuccess: () => {
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });

  const setFilter = (key: 'product' | 'status', value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader title={t('batches.filter.title')} description={t('batches.filter.description')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={t('batches.filter.product')}
            value={productId === null ? '' : String(productId)}
            onChange={(value) => {
              setFilter('product', value);
            }}
            options={[
              { value: '', label: t('batches.filter.allProducts') },
              ...products.items.map((product) => ({
                value: String(product.id),
                label: `${product.sku} — ${product.name}`,
              })),
            ]}
          />
          <SelectField
            label={t('batches.filter.status')}
            value={status ?? ''}
            onChange={(value) => {
              setFilter('status', value);
            }}
            options={[
              { value: '', label: t('batches.filter.allStatuses') },
              ...BATCH_STATUSES.map((value) => ({
                value,
                label: t(BATCH_STATUS_LABEL_KEYS[value]),
              })),
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('batches.title')}
          description={t('batches.description')}
          actions={
            <>
              {batches.items.length > 0 && (
                <Badge tone="neutral">{t('batches.count', { count: batches.items.length })}</Badge>
              )}
              {can('analyst') && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    create.reset();
                    setCreating(true);
                  }}
                >
                  {t('batches.new')}
                </Button>
              )}
            </>
          }
        />

        {query.isPending && <LoadingState rows={6} />}

        {query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!query.isPending && !query.isError && batches.items.length === 0 && (
          <EmptyState title={t('batches.empty')} detail={t('batches.empty.detail')} />
        )}

        {batches.items.length > 0 && (
          <>
            <Table caption={t('batches.description')}>
              <thead>
                <tr>
                  <Th>{t('batches.column.lot')}</Th>
                  <Th>{t('batches.column.product')}</Th>
                  <Th>{t('batches.column.expiry')}</Th>
                  <Th>{t('batches.column.territory')}</Th>
                  <Th className="text-right">{t('batches.column.activated')}</Th>
                  <Th>{t('batches.column.risk')}</Th>
                  <Th>{t('batches.column.status')}</Th>
                  <Th>
                    <span className="sr-only">{t('batches.column.actions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {batches.items.map((batch) => (
                  <tr key={batch.id}>
                    <Td>
                      <Link
                        to={`/batches/${String(batch.id)}`}
                        className="font-mono font-medium text-accent underline underline-offset-2"
                      >
                        {batch.lot_code}
                      </Link>
                    </Td>
                    <Td className="text-ink-muted">
                      {batch.product_sku ?? `#${String(batch.product_id)}`}
                    </Td>
                    <Td>{fmt.expiry(batch.expiry_date)}</Td>
                    <Td className="text-ink-muted">
                      {batch.territory ?? t('batches.territory.none')}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {fmt.number(batch.activated_count)}
                    </Td>
                    <Td>
                      <Badge tone={riskTone(batch.risk_score)}>{fmt.number(batch.risk_score)}</Badge>
                    </Td>
                    <Td>
                      <Badge
                        tone={isBatchStatus(batch.status) ? BATCH_STATUS_TONES[batch.status] : 'neutral'}
                      >
                        {isBatchStatus(batch.status)
                          ? t(BATCH_STATUS_LABEL_KEYS[batch.status])
                          : batch.status}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      {/* El retiro exige `admin` (`require_role(Role.ADMIN)`).
                          Está acá, en la fila, y no detrás de una navegación:
                          ése es el "un click" del criterio de aceptación. */}
                      {can('admin') && batch.status !== 'recalled' && (
                        <Button
                          size="sm"
                          variant="critical"
                          onClick={() => {
                            setRecalling(batch);
                          }}
                        >
                          {t('batches.recall')}
                        </Button>
                      )}
                      {batch.status === 'recalled' && (
                        <span className="text-xs text-ink-subtle">
                          {fmt.date(batch.recalled_at)}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {batches.hasMore ? (
                <Button size="sm" onClick={batches.loadMore} disabled={batches.isLoadingMore}>
                  {batches.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>

      <Dialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        title={t('batches.new.title')}
        description={t('batches.new.description')}
      >
        <BatchForm
          products={products.items}
          defaultProductId={productId}
          onSubmit={(draft) => {
            create.mutate(draft);
          }}
          onCancel={() => {
            setCreating(false);
          }}
          isSubmitting={create.isPending}
          error={create.error}
        />
      </Dialog>

      {/* El diálogo de retiro se monta con el lote elegido y se desmonta al
          cerrar: así el estado interno (paso, motivo, lote tipeado) nace
          limpio para cada lote y nunca se arrastra de uno al siguiente. */}
      {recalling !== null && (
        <RecallDialog
          batch={recalling}
          onClose={() => {
            setRecalling(null);
          }}
        />
      )}
    </div>
  );
}
